// Business setup checklist: questions about what the business does and the
// tailored to-do list they produce. All wording comes from the server
// (backend/config/business-setup-checklist.json).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { businessesAPI } from '../../services/api';
import { formatApiError } from '../../utils/errorMessage';

const STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS',
  'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC',
  'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'PR', 'GU', 'VI', 'AS', 'MP'
];

export function progressText(checklist) {
  if (!checklist || !checklist.total) return '';
  return `${checklist.done} of ${checklist.total} done`;
}

export function SetupQuestions({ businessId, onDone, onSkip, saveLabel = 'Build my checklist' }) {
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    Promise.all([
      businessesAPI.getSetupQuestions(),
      businessesAPI.getSetupChecklist(businessId)
    ]).then(([q, c]) => {
      if (!live) return;
      setQuestions(q.data.questions || []);
      setAnswers((c.data && c.data.answers) || {});
    }).catch((err) => live && setError(formatApiError(err, 'Could not load the questions')));
    return () => { live = false; };
  }, [businessId]);

  const toggleMulti = (id, value) => setAnswers((current) => {
    const list = Array.isArray(current[id]) ? current[id] : [];
    return { ...current, [id]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] };
  });

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await businessesAPI.saveSetupAnswers(businessId, answers);
      if (onDone) onDone(res.data);
    } catch (err) {
      setError(formatApiError(err, 'Could not save your answers'));
    } finally {
      setSaving(false);
    }
  };

  if (!questions) return error ? <p className="text-sm text-red-700">{error}</p> : <p className="text-sm text-gray-600">Loading...</p>;

  return (
    <form data-setup-questions="true" onSubmit={save} className="space-y-4">
      {questions.map((q) => (
        <fieldset key={q.id}>
          <legend className="text-sm font-medium text-gray-800">{q.label}</legend>
          {q.help && <p className="text-xs text-gray-500 mb-1">{q.help}</p>}
          {q.type === 'state' ? (
            <select
              value={answers[q.id] || ''}
              onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              aria-label={q.label}
            >
              <option value="">Choose a state</option>
              {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <div className="flex flex-wrap gap-2">
              {q.options.map((o) => {
                const chosen = q.type === 'multi'
                  ? Array.isArray(answers[q.id]) && answers[q.id].includes(o.value)
                  : answers[q.id] === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    role={q.type === 'multi' ? 'checkbox' : 'radio'}
                    aria-checked={chosen}
                    onClick={() => (q.type === 'multi'
                      ? toggleMulti(q.id, o.value)
                      : setAnswers({ ...answers, [q.id]: o.value }))}
                    className={`px-3 py-1 rounded-full border text-sm ${chosen ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          )}
        </fieldset>
      ))}
      {error && <p className="text-sm text-red-700">{error}</p>}
      <div className="flex justify-end gap-3">
        {onSkip && (
          <button type="button" onClick={onSkip} className="px-4 py-2 border rounded hover:bg-gray-50">Skip for now</button>
        )}
        <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          {saving ? 'Saving...' : saveLabel}
        </button>
      </div>
    </form>
  );
}

export function SetupChecklist({ businessId, onOpenDocuments, onLoaded }) {
  const [checklist, setChecklist] = useState(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState('');

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const apply = useCallback((data) => {
    setChecklist(data);
    if (onLoadedRef.current) onLoadedRef.current(data);
  }, []);

  useEffect(() => {
    let live = true;
    businessesAPI.getSetupChecklist(businessId)
      .then((res) => live && apply(res.data))
      .catch((err) => live && setError(formatApiError(err, 'Could not load the checklist')));
    return () => { live = false; };
  }, [businessId, apply]);

  const toggle = async (item) => {
    if (item.checked_by_document) return;
    setError('');
    try {
      const res = await businessesAPI.setSetupItem(businessId, item.key, !item.checked);
      apply(res.data);
    } catch (err) {
      setError(formatApiError(err, 'Could not save that'));
    }
  };

  if (!checklist) return error ? <p className="text-sm text-red-700">{error}</p> : <p className="text-sm text-gray-600">Loading...</p>;
  if (!checklist.applicable) return <p className="text-sm text-gray-600">Personal locations don't need a business setup checklist.</p>;

  if (editing || !checklist.answered) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-gray-700">Answer a few quick questions and we'll list what your business needs.</p>
        <SetupQuestions
          businessId={businessId}
          saveLabel={checklist.answered ? 'Update my checklist' : 'Build my checklist'}
          onDone={(data) => { apply(data); setEditing(false); }}
          onSkip={checklist.answered ? () => setEditing(false) : null}
        />
      </div>
    );
  }

  const pct = checklist.total ? Math.round((checklist.done / checklist.total) * 100) : 0;
  return (
    <div data-setup-checklist="true" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-800">{progressText(checklist)}</span>
        <button type="button" onClick={() => setEditing(true)} className="text-sm text-blue-700 underline">Change answers</button>
      </div>
      <div className="h-2 bg-gray-200 rounded" aria-hidden="true">
        <div className="h-2 bg-green-600 rounded" style={{ width: `${pct}%` }} />
      </div>
      <ul className="divide-y divide-gray-200">
        {checklist.items.map((item) => (
          <li key={item.key} className="py-2 flex items-start gap-3">
            <input
              type="checkbox"
              checked={item.checked}
              disabled={item.checked_by_document}
              onChange={() => toggle(item)}
              className="mt-1"
              aria-label={item.label}
            />
            <div className="flex-1">
              <div className={`text-sm font-medium ${item.checked ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{item.label}</div>
              <p className="text-xs text-gray-600">{item.help}</p>
              {item.document_kind && (
                item.checked_by_document
                  ? <p className="text-xs text-green-700">Checked automatically: the document is uploaded.</p>
                  : onOpenDocuments && (
                    <button type="button" onClick={onOpenDocuments} className="text-xs text-blue-700 underline">Upload it</button>
                  )
              )}
            </div>
          </li>
        ))}
      </ul>
      {error && <p className="text-sm text-red-700">{error}</p>}
      {checklist.notice && <p className="text-xs text-gray-500">{checklist.notice}</p>}
    </div>
  );
}

// "5 of 9 done" for a tab label; refreshKey reloads it after a change.
export function SetupProgressLabel({ businessId, refreshKey }) {
  const [text, setText] = useState('');
  useEffect(() => {
    let live = true;
    if (!businessId) return undefined;
    businessesAPI.getSetupChecklist(businessId)
      .then((res) => live && setText(res.data && res.data.answered ? progressText(res.data) : ''))
      .catch(() => live && setText(''));
    return () => { live = false; };
  }, [businessId, refreshKey]);
  return text ? <span data-setup-progress="true"> ({text})</span> : null;
}

export function SetupChecklistCard({ businessId }) {
  const [checklist, setChecklist] = useState(null);

  useEffect(() => {
    let live = true;
    if (!businessId) return undefined;
    businessesAPI.getSetupChecklist(businessId)
      .then((res) => live && setChecklist(res.data))
      .catch(() => live && setChecklist(null));
    return () => { live = false; };
  }, [businessId]);

  if (!checklist || !checklist.applicable || checklist.dismissed) return null;
  if (checklist.answered && checklist.done >= checklist.total) return null;

  const hide = async () => {
    setChecklist({ ...checklist, dismissed: true });
    try { await businessesAPI.setSetupDismissed(businessId, true); } catch (err) { /* hidden for this visit */ }
  };

  return (
    <section data-setup-card="true" aria-labelledby="setup-card-title" className="bg-white border border-gray-200 rounded-lg shadow p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 id="setup-card-title" className="text-base font-semibold text-gray-900">Business setup checklist</h2>
          <p className="mt-1 text-sm text-gray-700">
            {checklist.answered
              ? `${progressText(checklist)}. Keep going to make sure your business has what it needs.`
              : 'Answer a few quick questions and get a simple list of what your business needs.'}
          </p>
        </div>
        <button type="button" onClick={hide} className="text-sm text-gray-600 hover:text-gray-900">Hide</button>
      </div>
      <div className="mt-3">
        <Link to="/business?tab=setup" className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700">
          {checklist.answered ? 'Open checklist' : 'Start'}
        </Link>
      </div>
    </section>
  );
}

export default SetupChecklist;
